import firebase from "firebase/app";
import { SchoolCategory } from "./school";
import { CRAM_SCHOOL_TARGET_KEY } from "./target";

export type TargetPresetPrompt = {
  availableToAllStudents: boolean;
  /** 配布したアプリをホームに公開するか。この項目より前に保存された行には無い */
  isEnabled?: boolean;
  displayCategoryRef: firebase.firestore.DocumentReference;
  promptRef: firebase.firestore.DocumentReference;
  order?: number;
};

/** 未設定の行は、この項目を追加する前と同じく公開で配布する */
export function isPresetPromptEnabled(row: { isEnabled?: boolean }): boolean {
  return row.isEnabled ?? true;
}

export const schoolCreateDefaultPresetCollectionKey =
  "schoolCreateDefaultPresets";
export const schoolCreateDefaultPresetCategoryCollectionKey =
  "schoolCreateDefaultPresetCategories";
export const schoolCreateDefaultAppJobsCollectionKey =
  "schoolCreateDefaultAppJobs";
export const schoolCreateSegmentsCollectionKey = "schoolCreateSegments";

export type SchoolCreateSegment = { id: string; title: string; order: number };

export function sortSchoolCreateSegments(
  segments: SchoolCreateSegment[]
): SchoolCreateSegment[] {
  return [...segments].sort(
    (a, b) => a.order - b.order || a.id.localeCompare(b.id)
  );
}

export type SchoolCreatePresetKind = "default" | "segment";

export const SCHOOL_CREATE_DEFAULT_PRESET_IDS = {
  elementary: "elementary",
  juniorHigh: "juniorHigh"
} as const;

export type SchoolCreateDefaultPresetId = string;

export const SCHOOL_CREATE_DEFAULT_PRESET_TRIGGERS = {
  /** スクール作成時に配布する（既定） */
  schoolCreate: "schoolCreate",
  /** 塾向け機能を有効にしたときに配布する（領域「塾」のプリセット用） */
  jukuFeaturesEnabled: "jukuFeaturesEnabled"
} as const;

export type SchoolCreateDefaultPresetDistributionTrigger =
  (typeof SCHOOL_CREATE_DEFAULT_PRESET_TRIGGERS)[keyof typeof SCHOOL_CREATE_DEFAULT_PRESET_TRIGGERS];

export function convertToDistributionTrigger(
  value: unknown
): SchoolCreateDefaultPresetDistributionTrigger {
  return value === SCHOOL_CREATE_DEFAULT_PRESET_TRIGGERS.jukuFeaturesEnabled
    ? SCHOOL_CREATE_DEFAULT_PRESET_TRIGGERS.jukuFeaturesEnabled
    : SCHOOL_CREATE_DEFAULT_PRESET_TRIGGERS.schoolCreate;
}

export const SCHOOL_CREATE_DEFAULT_UNIFIED_FOLDER = {
  targetPresetId: "schoolCreateDefault",
  name: "デフォルトアプリ"
} as const;

/**
 * 対象プリセットが複数あるスクールでは共通フォルダにまとめ、
 * 1 つだけのときはそのプリセット名のフォルダを返す。
 */
export function resolveSchoolCreateDefaultFolder(
  presets: SchoolCreateDefaultPreset[],
  currentPresetId: SchoolCreateDefaultPresetId
): { targetPresetId: string; name: string } {
  if (presets.length > 1) return SCHOOL_CREATE_DEFAULT_UNIFIED_FOLDER;
  const current =
    presets.find(preset => preset.ref.id === currentPresetId) ?? presets[0];
  return {
    targetPresetId: currentPresetId,
    name: current?.data.title || SCHOOL_CREATE_DEFAULT_UNIFIED_FOLDER.name
  };
}

export type SchoolCreateDefaultPreset = {
  ref: firebase.firestore.DocumentReference;
  data: SchoolCreateDefaultPresetData;
};

export type SchoolCreateDefaultPresetData = {
  title: string;
  presetKind: SchoolCreatePresetKind;
  segmentId?: string;
  /** 対象のスクール領域 (targets.key)。空文字はどのスクールにも合致しない */
  targetKey: string;
  /** 対象の学校区分。空配列は領域内のすべての区分に合致する */
  schoolCategories: SchoolCategory[];
  /** 配布タイミング。既定はスクール作成時 */
  distributionTrigger: SchoolCreateDefaultPresetDistributionTrigger;
  prompts: TargetPresetPrompt[];
  createdAt: number;
  updatedAt: number;
};

/**
 * targetKey / schoolCategories 導入前に保存された固定 ID ドキュメント
 * （elementary / juniorHigh）の振り分け条件。converter がフィールド欠落時の補完に使う。
 */
const LEGACY_PRESET_MATCH_DEFAULTS: Record<
  string,
  { targetKey: string; schoolCategories: SchoolCategory[] }
> = {
  [SCHOOL_CREATE_DEFAULT_PRESET_IDS.elementary]: {
    targetKey: "school",
    schoolCategories: ["elementary", "elementary_junior"]
  },
  [SCHOOL_CREATE_DEFAULT_PRESET_IDS.juniorHigh]: {
    targetKey: "school",
    schoolCategories: [
      "junior_high",
      "high",
      "junior_high_school",
      "elementary_junior"
    ]
  }
};

export function convertToSchoolCreateDefaultPreset(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): SchoolCreateDefaultPreset {
  const legacy = LEGACY_PRESET_MATCH_DEFAULTS[ref.id];
  return {
    ref,
    data: {
      title: data.title ?? "",
      presetKind: data.presetKind === "segment" ? "segment" : "default",
      ...(typeof data.segmentId === "string"
        ? { segmentId: data.segmentId }
        : {}),
      targetKey: data.targetKey ?? legacy?.targetKey ?? "",
      schoolCategories: data.schoolCategories ?? legacy?.schoolCategories ?? [],
      distributionTrigger: convertToDistributionTrigger(
        data.distributionTrigger
      ),
      prompts: data.prompts ?? [],
      createdAt: data.createdAt ?? 0,
      updatedAt: data.updatedAt ?? 0
    }
  };
}

/**
 * 一覧・複製処理で使う安定した並び順（作成順 → ID 順）。
 */
export function sortSchoolCreateDefaultPresets(
  presets: SchoolCreateDefaultPreset[]
): SchoolCreateDefaultPreset[] {
  return [...presets].sort(
    (a, b) =>
      a.data.createdAt - b.data.createdAt || a.ref.id.localeCompare(b.ref.id)
  );
}

export type SchoolCreateDefaultPresetCategory = {
  ref: firebase.firestore.DocumentReference;
  data: SchoolCreateDefaultPresetCategoryData;
};

export type SchoolCreateDefaultPresetCategoryData = {
  title: string;
  createdAt: number;
  updatedAt: number;
};

export function convertToSchoolCreateDefaultPresetCategory(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): SchoolCreateDefaultPresetCategory {
  return {
    ref,
    data: {
      title: data.title,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt
    }
  };
}

export type SchoolCreateDefaultAppJobStatus =
  | "pending"
  | "running"
  | "succeeded"
  | "skipped"
  | "failed";

export type SchoolCreateDefaultAppJobFailedItem = {
  originPromptId: string;
  presetId: SchoolCreateDefaultPresetId;
  distributionKey?: string;
  message: string;
};

export type SchoolCreateSegmentRowSnapshot = {
  presetId: string;
  presetTitle: string;
  row: TargetPresetPrompt;
};

export type SchoolCreateDefaultAppJob = {
  ref: firebase.firestore.DocumentReference;
  data: SchoolCreateDefaultAppJobData;
};

export type SchoolCreateDefaultAppJobData = {
  status: SchoolCreateDefaultAppJobStatus;
  /**
   * 配布先スクールの doc ID。
   * 旧ドキュメントには無い（job doc ID = スクール doc ID だった）ため、
   * converter が空文字のときは doc ID を配布先として扱う。
   */
  schoolDocId: string;
  /** この job を起動した配布タイミング */
  trigger: SchoolCreateDefaultPresetDistributionTrigger;
  targetKey: string;
  schoolCategory: SchoolCategory | null;
  ownerUid: string;
  segmentIds?: string[];
  segmentRowsSnapshot?: SchoolCreateSegmentRowSnapshot[];
  completedDistributionKeys?: string[];
  presetIds: SchoolCreateDefaultPresetId[];
  completedOriginPromptIds: string[];
  failedItems: SchoolCreateDefaultAppJobFailedItem[];
  error?: string;
  createdAt: number;
  updatedAt?: number;
};

export function convertToSchoolCreateDefaultAppJob(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): SchoolCreateDefaultAppJob {
  const _data: SchoolCreateDefaultAppJobData = {
    status: data.status,
    schoolDocId: data.schoolDocId ?? "",
    trigger: convertToDistributionTrigger(data.trigger),
    targetKey: data.targetKey ?? "",
    schoolCategory: data.schoolCategory ?? null,
    ownerUid: data.ownerUid,
    segmentIds: Array.isArray(data.segmentIds) ? data.segmentIds : [],
    ...(Array.isArray(data.segmentRowsSnapshot)
      ? { segmentRowsSnapshot: data.segmentRowsSnapshot }
      : {}),
    completedDistributionKeys: data.completedDistributionKeys ?? [],
    presetIds: data.presetIds ?? [],
    completedOriginPromptIds: data.completedOriginPromptIds ?? [],
    failedItems: data.failedItems ?? [],
    createdAt: data.createdAt
  };
  if (typeof data.error === "string") {
    _data.error = data.error;
  }
  if (typeof data.updatedAt === "number") {
    _data.updatedAt = data.updatedAt;
  }
  return { ref, data: _data };
}

/**
 * 配布タイミングごとの job doc ID。
 * スクール作成時はスクール doc ID そのまま（既存互換）、
 * それ以外はサフィックス付きで別ドキュメントにする。
 */
export function schoolCreateDefaultAppJobId(
  schoolDocId: string,
  trigger: SchoolCreateDefaultPresetDistributionTrigger
): string {
  return trigger === SCHOOL_CREATE_DEFAULT_PRESET_TRIGGERS.schoolCreate
    ? schoolDocId
    : `${schoolDocId}_${trigger}`;
}

/**
 * スクールの領域・学校区分・配布タイミングに合致するプリセットを返す。
 * schoolCategories が空のプリセットは、その領域のすべてのスクールに合致する。
 */
export function matchSchoolCreateDefaultPresets(
  presets: SchoolCreateDefaultPreset[],
  targetKey: string | null | undefined,
  schoolCategory: SchoolCategory | null | undefined,
  trigger: SchoolCreateDefaultPresetDistributionTrigger
): SchoolCreateDefaultPreset[] {
  if (!targetKey) return [];
  return sortSchoolCreateDefaultPresets(presets).filter(preset => {
    if (preset.data.presetKind === "segment") return false;
    if (preset.data.distributionTrigger !== trigger) return false;
    if (!preset.data.targetKey || preset.data.targetKey !== targetKey) {
      return false;
    }
    if (preset.data.schoolCategories.length === 0) return true;
    if (!schoolCategory) return false;
    return preset.data.schoolCategories.includes(schoolCategory);
  });
}

/** セグメントを指定できるスクール領域 (targets.key) */
export const SEGMENT_TARGET_KEYS: readonly string[] = [
  "school",
  CRAM_SCHOOL_TARGET_KEY
];

export function isSegmentTargetKey(
  targetKey: string | null | undefined
): boolean {
  return !!targetKey && SEGMENT_TARGET_KEYS.includes(targetKey);
}

export function matchSchoolCreateSegmentPresets(
  presets: SchoolCreateDefaultPreset[],
  targetKey: string | null | undefined,
  segmentIds: string[]
): SchoolCreateDefaultPreset[] {
  if (!isSegmentTargetKey(targetKey) || segmentIds.length === 0) return [];
  const selected = new Set(segmentIds);
  return sortSchoolCreateDefaultPresets(presets).filter(
    preset =>
      preset.data.presetKind === "segment" &&
      preset.data.targetKey === targetKey &&
      preset.data.distributionTrigger ===
        SCHOOL_CREATE_DEFAULT_PRESET_TRIGGERS.schoolCreate &&
      !!preset.data.segmentId &&
      selected.has(preset.data.segmentId)
  );
}

export type SchoolCreateDefaultPresetPromptRow = {
  presetId: SchoolCreateDefaultPresetId;
  row: TargetPresetPrompt;
};

/**
 * 複数プリセットに同じ promptRef がある場合は先勝ちで 1 件にする。
 * 小中一貫で小学校用と中高用の両方に同じアプリが入っているケース向け。
 */
export function collectUniquePresetPrompts(
  presets: SchoolCreateDefaultPreset[]
): SchoolCreateDefaultPresetPromptRow[] {
  const seen = new Set<string>();
  const result: SchoolCreateDefaultPresetPromptRow[] = [];
  for (const preset of presets) {
    const sorted = [...preset.data.prompts].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    for (const row of sorted) {
      const originId = row.promptRef.id;
      if (!originId || seen.has(originId)) continue;
      seen.add(originId);
      result.push({
        presetId: preset.ref.id,
        row
      });
    }
  }
  return result;
}

export type SchoolCreateDistributionRow = SchoolCreateDefaultPresetPromptRow & {
  distributionKey: string;
};

/** 同一原本は全プリセットを通して 1 件にする。デフォルトの行を優先する。 */
export function collectSchoolCreateDistributionRows(
  defaultPresets: SchoolCreateDefaultPreset[],
  segmentRowsSnapshot: SchoolCreateSegmentRowSnapshot[]
): SchoolCreateDistributionRow[] {
  const seen = new Set<string>();
  return [
    ...collectUniquePresetPrompts(defaultPresets).map(item => ({
      ...item,
      distributionKey: `default:${item.row.promptRef.id}`
    })),
    ...segmentRowsSnapshot.map(item => ({
      presetId: item.presetId,
      row: item.row,
      distributionKey: `segment:${item.row.promptRef.id}`
    }))
  ].filter(item => {
    const originId = item.row.promptRef.id;
    if (!originId || seen.has(originId)) return false;
    seen.add(originId);
    return true;
  });
}
